import { useCallback, useEffect, useRef } from 'react';
import { useShiftControls } from './shift-controller';
import { ACTIVITY_EVENT_THROTTLE_MS } from './util';
import { useShiftStore } from './shift-store';
import { useDebouncedCallback } from 'use-debounce';
import { useShiftBroadcastChannel } from './shift-broadcast';

/**
 * On user activity, update last active and ACK periodically.
 *
 * Should be scoped to the app areas intended to update tracking state
 * i.e. monitoring portal.
 */
export const ShiftActivityTracking = () => {
  const controls = useShiftControls();
  const channel = useShiftBroadcastChannel();
  const lastUpdate = useRef<Date>();

  const throttledAck = useDebouncedCallback(
    () => {
      controls.ack();
    },
    10 * 1000,
    {
      leading: true,
      trailing: true,
      maxWait: 30 * 1000,
    }
  );

  const registerAction = useCallback(() => {
    console.log('[Idleness] Idleness onAction triggered');
    const { breakStartedAt, status } = useShiftStore.getState();

    if (breakStartedAt != null) {
      console.debug('[Idleness] Not registering user action, as on break.');
      return;
    }

    if (status != 'active') {
      console.debug('[Idleness] Not registering user action, as not active.');
      return;
    }

    const lastActiveAt = new Date();
    useShiftStore.setState({ lastActiveAt, status: 'active' });
    channel.sendMessage({ type: 'user.activity', time: lastActiveAt });
    throttledAck();
  }, [throttledAck, channel]);

  useEffect(() => {
    const listener = () => {
      const diff = lastUpdate.current ? Date.now() - lastUpdate.current.getTime() : Infinity;
      const isThrottled = diff < ACTIVITY_EVENT_THROTTLE_MS;
      if (isThrottled) return;

      lastUpdate.current = new Date();
      registerAction();
    };

    document.addEventListener('click', listener);
    document.addEventListener('keydown', listener);
    document.addEventListener('scrollend', listener);

    return () => {
      document.removeEventListener('click', listener);
      document.removeEventListener('keydown', listener);
      document.removeEventListener('scrollend', listener);
    };
  }, []);

  return null;
};
